import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import PhotoPlaceholder from "../components/PhotoPlaceholder";
import SEO from "../components/SEO";
import ChapterHero from "../components/ChapterHero";
import NextPage from "../components/NextPage";
import AudiobookRadio from "../components/AudiobookRadio";
import { getStoryBarnItems, getColoringPages } from "../lib/api";
import { getShopProducts, getShopConfig, assetUrl } from "../lib/shopApi";
import { cart } from "../lib/cart";
import { ShopProductCard } from "../components/shop/ShopProductCard";
import { ProductDialog } from "../components/shop/ProductDialog";
import { CartButton } from "../components/shop/CartButton";
import { CartDrawer } from "../components/shop/CartDrawer";
import { CollectionDialog } from "../components/shop/CollectionDialog";

const SHOP_TINTS = ["#579DE6", "#2DE181", "#4B3A2F", "#D62839", "#C8A876"];

export default function StoryBarn() {
  const [items, setItems] = useState([]);
  const [coloring, setColoring] = useState([]);
  const [shopProducts, setShopProducts] = useState([]);
  const [testMode, setTestMode] = useState(false);
  const [openProduct, setOpenProduct] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [openCollection, setOpenCollection] = useState(null);
  const [params, setParams] = useSearchParams();
  const collectionSlugs = new Set(items.map((i) => i.slug));
  const inCollection = (slug) => shopProducts.filter((p) => p.collection === slug);
  const looseProducts = shopProducts.filter((p) => !collectionSlugs.has(p.collection));

  useEffect(() => {
    getStoryBarnItems().then(setItems).catch(() => setItems([]));
    getColoringPages().then(setColoring).catch(() => setColoring([]));
    getShopProducts()
      .then((list) => {
        setShopProducts(list);
        cart.prune(list.filter((p) => p.status === "live"));
      })
      .catch(() => setShopProducts([]));
    getShopConfig().then((c) => setTestMode(c.test_mode)).catch(() => {});
  }, []);

  useEffect(() => {
    if (params.get("checkout") === "cancelled") {
      toast("Checkout cancelled — your basket is still here.");
      setParams({}, { replace: true });
      setCartOpen(true);
    }
  }, [params, setParams]);

  return (
    <div className="parchment min-h-screen" data-testid="story-barn-page">
      <SEO
        title="The Story Barn & Mercantile™"
        description="Books, coloring pages, read-aloud experiences, and small handcrafted treasures from the pasture. The Story Barn™ doors are opening soon."
        path="/story-barn"
      />

      <ChapterHero
        path="/story-barn"
        image="/assets/heroes/story-barn.webp"
        title={
          <>
            The Story Barn
            <span
              className="block text-center font-display"
              style={{ fontSize: "0.55em", lineHeight: 1, margin: "0.05em 0" }}
              aria-hidden="true"
            >
              &amp;
            </span>
            Mercantile™
          </>
        }
        titleAlt="The Story Barn & Mercantile"
        subtitle="Books and goodness, all under one roof."
        position="top-left"
        size="sm"
      />

      <div className="relative max-w-6xl mx-auto px-6 -mt-8 z-10 pb-24">
        {/* Intro line */}
        <p className="font-body text-2xl text-[#2C2724]/85 text-center max-w-3xl mx-auto leading-relaxed mb-16">
          Step inside. The shelves are still being filled with books,
          coloring pages, read-aloud experiences, and small handcrafted
          treasures from the pasture. Wander gently &mdash; more is coming.
        </p>

        {/* Product / Coming-Soon grid */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7"
          data-testid="story-barn-grid"
        >
          {items
            .filter((item) => {
              // Coloring pages now live in the dedicated "Coloring Corner"
              // section below — filter any product card that would create
              // a duplicate listing.
              const slug = (item.slug || "").toLowerCase();
              const title = (item.title || "").toLowerCase();
              const kind = (item.kind || "").toLowerCase();
              return !(
                slug.includes("coloring") ||
                title.includes("coloring") ||
                kind.includes("coloring")
              );
            })
            .map((item, i) => {
            const tints = [
              { kind: "#579DE6", bar: "#579DE6" },
              { kind: "#2DE181", bar: "#2DE181" },
              { kind: "#4B3A2F", bar: "#4B3A2F" },
              { kind: "#D62839", bar: "#D62839" },
              { kind: "#C8A876", bar: "#C8A876" },
              { kind: "#D62839", bar: "#D62839" },
            ];
            const t = tints[i % tints.length];
            const colProducts = inCollection(item.slug);
            const colLive = colProducts.some((p) => p.status === "live");
            const colCover = colProducts.find((p) => p.status === "live" && p.images?.length)?.images[0];
            const isAvailable = item.status === "available" || colLive;
            const statusLabel = isAvailable ? "Available" : "Coming Soon";

            // The "audiobook" product card is swapped for the wooden-radio
            // player so listeners can press play right from the barn shelf.
            if (item.slug === "audiobook") {
              return (
                <div
                  key={item.slug}
                  data-testid={`barn-item-${item.slug}`}
                  className="sm:col-span-2 lg:col-span-2 flex flex-col justify-center items-center gap-5"
                >
                  <div className="w-full"><AudiobookRadio /></div>
                  {item.status !== "available" && (
                    <span
                      data-testid="barn-item-status-audiobook"
                      className="inline-block px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.2em] text-white coming-soon-flash"
                      style={{ background: "#4B3A2F" }}
                    >
                      Coming Soon
                    </span>
                  )}
                </div>
              );
            }

            return (
              <article
                key={item.slug}
                data-testid={`barn-item-${item.slug}`}
                className="relative flex flex-col bg-[#F9F6F0] border border-[#E7DFD5] rounded-md page-shadow overflow-hidden hover:-translate-y-[2px] transition-all duration-700"
              >
                <div
                  className="absolute top-0 left-0 right-0 h-1.5"
                  style={{ background: t.bar }}
                />
                {item.href && (
                  <Link
                    to={item.href}
                    aria-label={`Open ${item.title}`}
                    data-testid={`barn-item-link-${item.slug}`}
                    className="absolute inset-0 z-10"
                  />
                )}
                {!item.href && colLive && (
                  <button
                    type="button"
                    onClick={() => setOpenCollection({ item, tint: t.bar })}
                    aria-label={`Browse ${item.title}`}
                    data-testid={`barn-item-open-${item.slug}`}
                    className="absolute inset-0 z-10"
                  />
                )}
                <div className="p-4">
                  {colCover ? (
                    <div className="aspect-square bg-white rounded overflow-hidden">
                      <img src={assetUrl(colCover)} alt={item.title} className="w-full h-full object-contain" loading="lazy" />
                    </div>
                  ) : (
                    <PhotoPlaceholder
                      label={item.title}
                      aspect="square"
                      accent={t.bar}
                    />
                  )}
                </div>
                <div className="px-6 pb-6 flex flex-col flex-grow">
                  <p
                    className="text-xs uppercase tracking-[0.25em] mb-2"
                    style={{ color: t.kind }}
                  >
                    {item.kind}
                  </p>
                  <h3 className={`font-display text-2xl text-[#4B3A2F] leading-tight ${item.subtitle ? "" : "mb-2"}`}>
                    {item.title}
                  </h3>
                  {item.subtitle && (
                    <p className="font-display text-base text-[#4B3A2F]/75 leading-tight mb-2" data-testid={`barn-item-subtitle-${item.slug}`}>
                      {item.subtitle}
                    </p>
                  )}
                  <p className="font-body text-sm text-[#2C2724]/80 leading-relaxed flex-grow">
                    {item.note}
                  </p>
                  <div className="mt-5">
                    <span
                      data-testid={`barn-item-status-${item.slug}`}
                      className={`inline-block px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.2em] text-white ${!isAvailable ? "coming-soon-flash" : ""}`}
                      style={{
                        background: isAvailable ? "#7E9268" : t.bar,
                      }}
                    >
                      {statusLabel}
                    </span>
                  </div>
                </div>
              </article>
            );
          })}
          {looseProducts.map((product, i) => (
            <ShopProductCard
              key={product.id}
              product={product}
              tint={SHOP_TINTS[i % SHOP_TINTS.length]}
              onOpen={setOpenProduct}
            />
          ))}
        </div>

        {/* Coloring Corner — a stall inside the Story Barn */}
        {coloring.length > 0 && (
          <section
            data-testid="story-barn-coloring-corner"
            className="mt-24"
          >
            <div className="text-center mb-10">
              <h2 className="font-display text-3xl md:text-4xl text-[#4B3A2F]">
                The Coloring Corner
              </h2>
              <p className="font-script text-2xl text-[#4B3A2F] mt-2">
                free printable pages, straight from the barn
              </p>
              <div className="gold-rule mx-auto mt-4 w-24" />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
              {coloring.map((p, idx) => {
                const rawUrl = p.file_url || "";
                const url = rawUrl.startsWith("http")
                  ? rawUrl
                  : `${process.env.REACT_APP_BACKEND_URL || ""}${rawUrl}`;
                const isPdf = (rawUrl || "").toLowerCase().endsWith(".pdf");
                const key = p.id || p.slug || rawUrl || `coloring-${idx}`;
                return (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`barn-coloring-${key}`}
                    className="group flex flex-col bg-[#F9F6F0] border border-[#E7DFD5] rounded-md page-shadow overflow-hidden transition-transform duration-500 hover:-translate-y-[2px]"
                    download={isPdf ? p.title : undefined}
                  >
                    <div className="aspect-square bg-white overflow-hidden">
                      {isPdf ? (
                        <div className="w-full h-full flex items-center justify-center text-[#8D6E63] font-script text-2xl">
                          PDF
                        </div>
                      ) : (
                        <img
                          src={url}
                          alt={p.title}
                          className="w-full h-full object-contain group-hover:scale-[1.02] transition-transform duration-500"
                          draggable="false"
                        />
                      )}
                    </div>
                    <div className="p-4 text-center">
                      <p className="font-display text-lg text-[#4B3A2F] leading-tight">
                        {p.title}
                      </p>
                      {p.verse && (
                        <p className="font-script text-sm text-[#6D5A50] mt-1">
                          {p.verse}
                        </p>
                      )}
                      <p className="text-[10px] uppercase tracking-[0.25em] text-[#D62839] mt-3">
                        Download
                      </p>
                    </div>
                  </a>
                );
              })}
            </div>
          </section>
        )}

        {/* Closing beat — the last page of the book */}
        <section
          data-testid="story-barn-closing"
          className="max-w-2xl mx-auto text-center mt-24 pt-16 border-t border-[#E7DFD5]"
        >
          <div className="gold-rule mx-auto mb-10 w-32" />
          <p className="font-display italic text-2xl md:text-3xl text-[#4B3A2F] leading-[1.55]">
            Thank you for turning these pages with us. We hope you&rsquo;ll
            come back often&hellip; because God is still writing the story,
            one little idea at a time.
          </p>
          <div className="gold-rule mx-auto mt-10 w-32" />
          <p className="mt-10 text-[11px] uppercase tracking-[0.55em] text-[#D62839]">
            Where Faith Finds Its Salt
          </p>
        </section>
      </div>
      <NextPage to="/" label="Back to Where the Story Begins" />
      <CollectionDialog
        collection={openCollection?.item}
        tint={openCollection?.tint}
        products={openCollection ? inCollection(openCollection.item.slug) : []}
        onClose={() => setOpenCollection(null)}
        onOpenProduct={(p) => {
          setOpenCollection(null);
          setOpenProduct(p);
        }}
      />
      <ProductDialog
        product={openProduct}
        onClose={() => setOpenProduct(null)}
        onAdded={() => {
          setOpenProduct(null);
          setCartOpen(true);
        }}
      />
      <CartButton onClick={() => setCartOpen(true)} />
      <CartDrawer open={cartOpen} onOpenChange={setCartOpen} testMode={testMode} />
    </div>
  );
}
